import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import styles from '../BankDataTable.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import SkeletonCell from '../../../../../common/_custom/SkeletonCell/SkeletonCell';
import { infoIconGreen, infoIconYellow, warning } from '../../../../../assets/hostedassets';
import MissingDaysTooltipContent, { dedupeRanges, mergeAdjacentRanges } from './MissingDaysTooltipContent';
import MismatchPill from './MismatchPill';

interface RowsProps {
    rows: any;
    headers: any;
    tableRef: any;
    classificationPending?: boolean;
}

// Helper function to format missing date ranges for tooltip
const formatMissingDatesForTooltip = (fullAmountData: any) => {
    if (!fullAmountData) {
        return <span>Data for this period is not available.</span>;
    }
    const { missingDates, missingDays, missingDaysAccountsCount, totalAccountsCount, hoverInfo, balanceMismatchRanges } = fullAmountData;

    // A period can carry balance-mismatch ranges even when its own missing-day
    // metadata is empty — the tooltip must still render the mismatch section.
    const hasMismatchData =
        (balanceMismatchRanges?.length ?? 0) > 0 || hoverInfo?.some((account: any) => (account?.balanceMismatchRanges?.length ?? 0) > 0);

    if ((!missingDates || missingDates.length === 0 || missingDays === 0) && missingDaysAccountsCount === 0 && !hasMismatchData) {
        return '';
    }

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.getDate().toString();
    };

    const formatDateRange = (startDate: string, endDate: string) => {
        if (startDate === endDate) {
            return formatDate(startDate);
        } else {
            return `${formatDate(startDate)}-${formatDate(endDate)}`;
        }
    };

    // If hoverInfo exists and missingDaysAccountsCount > 0, show account-wise data
    if (hoverInfo && hoverInfo.length > 0 && missingDaysAccountsCount > 0) {
        const accountLines = hoverInfo.map((account) => {
            const bankName = account.accountName || '';
            const accountNumber = account.accountNumber || '';
            const dateRanges = account.missingDates?.map((range) => formatDateRange(range.startDate, range.endDate)) || [];
            const dateString = dateRanges.join(',');
            return (
                <div key={account.accountId} style={{ display: 'grid', gridTemplateColumns: '1fr 0.5fr', justifyContent: 'space-between' }}>
                    <span style={{ color: '#9D9D9D' }}>
                        {bankName} {accountNumber}
                    </span>
                    <span style={{ textAlign: 'right', width: '90%', whiteSpace: 'pre-wrap' }}>{dateString}</span>
                </div>
            );
        });

        // Balance mismatch lines for account-wise data
        const balanceMismatchAccountLines = hoverInfo
            .map((account) => {
                const bankName = account.accountName || '';
                const accountNumber = account.accountNumber || '';
                const balanceMismatchDateRanges =
                    account.balanceMismatchRanges?.map((range) => formatDateRange(range.startDate, range.endDate)) || [];
                const balanceMismatchDateString = balanceMismatchDateRanges.join(',');

                if (balanceMismatchDateRanges.length === 0) {
                    return null;
                }

                return (
                    <div
                        key={`balance-${account.accountId}`}
                        style={{ display: 'grid', gridTemplateColumns: '1fr 0.5fr', justifyContent: 'space-between' }}
                    >
                        <span style={{ color: '#9D9D9D' }}>
                            {bankName} {accountNumber}
                        </span>
                        <span style={{ textAlign: 'right', width: '90%', whiteSpace: 'pre-wrap' }}>{balanceMismatchDateString}</span>
                    </div>
                );
            })
            .filter(Boolean);

        return (
            <>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 0.5rem 0' }}>Missing Days</h3>
                <div style={{ fontSize: '12px', color: '#475467', whiteSpace: 'nowrap', minWidth: 'max-content' }}>
                    {accountLines.map((line, index) => (
                        <div
                            key={index}
                            style={{
                                marginBottom: index < accountLines.length - 1 ? '0.25rem' : '0',
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                            }}
                        >
                            {line}
                        </div>
                    ))}
                </div>

                {/* Balance Mismatch Section */}
                {balanceMismatchAccountLines.length > 0 && (
                    <>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0.75rem 0 0.5rem 0' }}>
                            Balance Mismatch
                        </h3>
                        <div style={{ fontSize: '12px', color: '#475467', whiteSpace: 'nowrap', minWidth: 'max-content' }}>
                            {balanceMismatchAccountLines.map((line, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginBottom: index < balanceMismatchAccountLines.length - 1 ? '0.25rem' : '0',
                                        whiteSpace: 'nowrap',
                                        overflow: 'visible',
                                    }}
                                >
                                    {line}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </>
        );
    }

    // Fallback to original behavior for non-aggregated (single account) view.
    // Missing-date ranges within the month are merged when contiguous; balance-mismatch
    // ranges arrive as full ranges (possibly spanning months) repeated under each month
    // they touch, so dedupe them.
    const missingRanges = mergeAdjacentRanges(missingDates || []);
    const mismatchRanges = dedupeRanges(balanceMismatchRanges || []);

    return <MissingDaysTooltipContent missingRanges={missingRanges} balanceMismatchRanges={mismatchRanges} />;
};

function Rows(props: RowsProps) {
    const { rows, headers, tableRef, classificationPending } = props;
    const [fullRowColspan, setFullRowColSpan] = useState(1);
    const bounceTransactionsStartIndex = 9;
    const creditsAndDebitsStartIndex = 15;
    const paymentMethodStartIndex = 19;

    useEffect(() => {
        if (tableRef?.current) {
            let colspan = 1;
            const mainCellWidth = 330;
            const dataCellWidth = 150;
            const tableWidth = tableRef?.current?.offsetWidth || 800;
            colspan += Math.round((tableWidth - mainCellWidth) / dataCellWidth) + 1;
            setFullRowColSpan(colspan);
        }
    }, [tableRef?.current]);

    const addSeparatorRow = (index) => {
        switch (index) {
            case creditsAndDebitsStartIndex:
            case bounceTransactionsStartIndex:
            case paymentMethodStartIndex:
                return true;
            default:
                return false;
        }
    };

    const transformedRows = rows?.map((row, index) => ({
        ...row,
        amountsMap: row?.amounts?.reduce((map, amount) => {
            map[amount?.period] = amount?.amountFormatted;
            // Store the full amount object for Missing Data rows to access missingDates
            if (row.classification === 'Missing Data') {
                map[`${amount?.period}_full`] = amount;
            }
            return map;
        }, {}),
    }));

    const formatAmount = (amount = '') => {
        if (!amount) return '-';
        else {
            let val = parseFloat(amount);
            if (val) return val < 0 ? `(${Math.round(-1 * val)?.toLocaleString('en-IN')})` : Math.round(val)?.toLocaleString();
            else return amount;
        }
    };

    const formatNumbers = (number: string): string => {
        if (!number) return '-';
        else if (number.startsWith('-')) return `(${number.slice(1)})`;
        return number;
    };

    // Check if current row is Missing Data
    const isMissingDataRow = (row) => {
        return row?.classification === 'Missing Data';
    };

    return (
        <>
            {transformedRows?.map?.((row, i) => (
                <React.Fragment key={i}>
                    {addSeparatorRow(i) && (
                        <>
                            <tr className={clsx(styles.SeparatorRow)} key={`${row?.classification}-${i}`}>
                                <td className={styles.SeparatorRowItem} colSpan={fullRowColspan}>
                                    {i === bounceTransactionsStartIndex && 'Bounce Transactions'}
                                    {i === creditsAndDebitsStartIndex && 'Credits and Debits'}
                                    {i === paymentMethodStartIndex && 'Payment Method'}
                                </td>
                            </tr>
                        </>
                    )}
                    <tr
                        key={i}
                        className={clsx(styles.TableBodyRow, {
                            [styles.MissingDataRow]: isMissingDataRow(row),
                        })}
                    >
                        {headers?.map((header, j) => {
                            const cellValue =
                                header?.key === 'classification' ? row?.classification : formatNumbers(row?.amountsMap?.[header?.name]);

                            // For Missing Data rows, wrap all cells with tooltips and icons
                            if (isMissingDataRow(row)) {
                                if (header?.key === 'classification') {
                                    return (
                                        <td
                                            className={clsx(styles.TableBodyRowItem, styles.MissingDataHeaderCell)}
                                            key={`${header?.name}-${i}`}
                                        >
                                            <div className={styles.MissingDataHeader}>
                                                <span>{cellValue}</span>
                                            </div>
                                        </td>
                                    );
                                } else {
                                    const fullAmountData = row?.amountsMap?.[`${header?.name}_full`];
                                    const hasMissingDays = fullAmountData?.missingDays > 0;
                                    const hasIncompleteData = hasMissingDays || fullAmountData?.missingDaysAccountsCount > 0;
                                    const hasBalanceMismatch =
                                        fullAmountData?.balanceMismatchRanges?.length > 0 ||
                                        fullAmountData?.hoverInfo?.some((account: any) => account?.balanceMismatchRanges?.length > 0);
                                    const tooltipContent = formatMissingDatesForTooltip(fullAmountData);

                                    // Chip severity: solid Mismatch pill when the month has a balance
                                    // mismatch, yellow when it only has missing days, green when complete.
                                    const chipColors = hasIncompleteData
                                        ? { backgroundColor: '#FEF9EC', color: '#E4AB01' }
                                        : { backgroundColor: '#E1FFE1', color: '#49A71B' };
                                    const chipIcon = hasIncompleteData ? infoIconYellow : infoIconGreen;
                                    const chipAlt = hasIncompleteData ? 'Missing data' : 'Complete data';

                                    return (
                                        <td
                                            className={clsx(styles.TableBodyRowItem, styles.MissingDataCell, {
                                                [styles.HasMissingData]: hasMissingDays,
                                                [styles.NoMissingData]: !hasMissingDays,
                                            })}
                                            key={`${header?.name}-${i}`}
                                            style={{ position: 'relative' }}
                                        >
                                            <Tooltip
                                                content={tooltipContent}
                                                direction="bottom"
                                                fitContent
                                                style={{
                                                    zIndex: 9999,
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    marginTop: '4px',
                                                    whiteSpace: 'nowrap',
                                                    padding: '0.5rem',
                                                    maxWidth: 'none',
                                                    width: 'max-content',
                                                    textAlign: 'left',
                                                    display: 'flex',
                                                }}
                                            >
                                                {hasBalanceMismatch ? (
                                                    <MismatchPill />
                                                ) : (
                                                    <div
                                                        style={{
                                                            ...chipColors,
                                                            borderRadius: '1rem',
                                                            padding: '0.25rem 0.5rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }}
                                                        className={styles.MissingDataCellContent}
                                                    >
                                                        <span className={styles.MissingDataValue}>{cellValue}</span>
                                                        {cellValue !== '-' && <img src={chipIcon} alt={chipAlt} width={12} height={12} />}
                                                    </div>
                                                )}
                                            </Tooltip>
                                        </td>
                                    );
                                }
                            }

                            // Regular cells for non-Missing Data rows
                            const showPendingLoader =
                                classificationPending && header?.key !== 'classification' && i >= paymentMethodStartIndex;

                            return (
                                <td
                                    className={clsx(styles.TableBodyRowItem, {
                                        [styles.negativeValue]: row?.amountsMap?.[header?.name]?.startsWith('-'),
                                    })}
                                    key={`${header?.name}-${i}`}
                                >
                                    {showPendingLoader ? <SkeletonCell /> : cellValue}
                                </td>
                            );
                        })}
                    </tr>
                </React.Fragment>
            ))}
        </>
    );
}

export default Rows;
